import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type {
  InventoryItem, StockMovement, InventoryKind, Location, MovementType,
} from '@/types/inventory';

// ─────────── Items ───────────

export type InventoryFilters = {
  kind?: InventoryKind;
  location?: Location;
  lowStock?: boolean;
  search?: string;
  limit?: number;
};

export function useInventory(filters: InventoryFilters = {}) {
  return useQuery({
    queryKey: ['inventory', filters],
    queryFn: async () => {
      const { data } = await api.get<{ items: InventoryItem[] }>('/inventory', { params: filters });
      return data.items;
    },
  });
}

export function useInventoryItem(id: string | undefined) {
  return useQuery({
    queryKey: ['inventory-item', id],
    queryFn: async () => (await api.get<InventoryItem>(`/inventory/${id}`)).data,
    enabled: !!id,
  });
}

export type SaveItemInput = Omit<InventoryItem,
  'id' | 'createdAt' | 'createdBy' | 'updatedAt' | 'updatedBy' | 'onHand' | 'totalOnHand' | 'valueAtCost'
> & {
  /** Optional initial count entered when creating a brand-new item */
  initialCount?: { quantity: number; location: Location };
};

export function useCreateInventoryItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: SaveItemInput) =>
      (await api.post<{ id: string }>('/inventory', input)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['inventory'] }),
  });
}

export function useUpdateInventoryItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...patch }: { id: string } & Partial<SaveItemInput>) =>
      (await api.patch(`/inventory/${id}`, patch)).data,
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['inventory'] });
      qc.invalidateQueries({ queryKey: ['inventory-item', vars.id] });
    },
  });
}

export function useDeleteInventoryItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => (await api.delete(`/inventory/${id}`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['inventory'] }),
  });
}

// ─────────── Movements ───────────

export function useItemMovements(itemId: string | undefined) {
  return useQuery({
    queryKey: ['movements', itemId],
    queryFn: async () => {
      const { data } = await api.get<{ items: StockMovement[] }>(`/inventory/${itemId}/movements`);
      return data.items;
    },
    enabled: !!itemId,
  });
}

export function useAllMovements(limit = 100) {
  return useQuery({
    queryKey: ['movements-all', limit],
    queryFn: async () => {
      const { data } = await api.get<{ items: StockMovement[] }>('/inventory/movements', { params: { limit } });
      return data.items;
    },
  });
}

export type RecordMovementInput = {
  itemId: string;
  type: MovementType;
  quantity: number;
  location: Location;
  toLocation?: Location;
  occurredAt?: string;
  notes?: string;
  orderId?: string;
  orderNumber?: string;
  poNumber?: string;
  batch?: string;
  lotNumber?: string;
  unitCostAtTime?: number;
  reason?: string;
};

export function useRecordMovement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: RecordMovementInput) =>
      (await api.post<{ id: string }>(`/inventory/${input.itemId}/movements`, input)).data,
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['inventory'] });
      qc.invalidateQueries({ queryKey: ['inventory-item', vars.itemId] });
      qc.invalidateQueries({ queryKey: ['movements', vars.itemId] });
      qc.invalidateQueries({ queryKey: ['movements-all'] });
    },
  });
}
